/**
 * Relatório técnico do fluxo.
 * Os números vêm de analyzeFlow e enumerateFlowPaths, o mesmo motor do Enterprise.
 */

import type { EnterpriseCardInput } from '../enterprise/enterpriseReportModel';
import {
  analyzeFlow,
  enumerateFlowPaths,
  formatExecutiveDuration,
  presentOwners,
  projectedVariation,
  scenarioIsComparable,
  type FlowPath,
  type FlowReading,
} from '../enterprise/enterpriseFlowAnalysis';

export interface TechnicalIndicatorRow {
  indicator: string;
  current: string;
  future: string;
  variation: string;
}

export interface TechnicalActivityRow {
  code: string;
  name: string;
  owner: string;
  probability: string;
  tp: string;
  tra: string;
  wait: string;
}

export interface TechnicalPathRow {
  code: string;
  title: string;
  sequence: string;
  probability: string;
  tp: string;
  tra: string;
  wait: string;
  pce: string;
  loopNote: string | null;
  tpMinutes: number | null;
  traMinutes: number | null;
  key: string;
}

export interface TechnicalPathComparisonRow {
  path: string;
  tpCurrent: string;
  traCurrent: string;
  tpFuture: string;
  traFuture: string;
  variation: string;
}

export interface TechnicalFlowReport {
  processName: string;
  organizationName: string | null;
  generatedAt: string;
  futureAvailable: boolean;
  futureNote: string | null;
  legend: string;
  indicators: TechnicalIndicatorRow[];
  currentActivities: TechnicalActivityRow[];
  futureActivities: TechnicalActivityRow[];
  currentPaths: TechnicalPathRow[];
  futurePaths: TechnicalPathRow[];
  pathComparison: TechnicalPathComparisonRow[];
  pathsTruncated: boolean;
}

const FUTURE_MISSING = 'Estado futuro não disponível.';
const ELIMINATED = 'Eliminado no Estado Futuro';
const CREATED = 'Novo no Estado Futuro';

function duration(minutes: number | null): string {
  if (minutes == null) return '-';
  return formatExecutiveDuration(minutes);
}

function percent(value: number | null): string {
  if (value == null) return '-';
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
  return `${text}%`;
}

function activitiesOf(flow: FlowReading): TechnicalActivityRow[] {
  return flow.stages.map((stage, index) => ({
    code: String(index + 1).padStart(2, '0'),
    name: stage.name,
    owner: stage.owner ? presentOwners([stage.owner]).join(' · ') : '-',
    probability: stage.frequencyLabel,
    tp: duration(stage.permanenceMinutes),
    tra: duration(stage.touchMinutes),
    wait: duration(stage.waitMinutes),
  }));
}

function pathsOf(paths: FlowPath[]): TechnicalPathRow[] {
  return paths.map((path) => ({
    code: path.code,
    title: path.title,
    sequence: path.sequence,
    probability: path.probabilityLabel,
    tp: duration(path.tpMinutes),
    tra: duration(path.traMinutes),
    wait: duration(path.waitMinutes),
    pce: percent(path.pcePercent),
    loopNote: path.loopNote,
    tpMinutes: path.tpMinutes,
    traMinutes: path.traMinutes,
    key: path.key,
  }));
}

function indicator(
  label: string,
  current: number | null,
  future: number | null,
  futureAvailable: boolean,
  kind: 'duration' | 'count' | 'points',
  format: (value: number) => string,
): TechnicalIndicatorRow {
  const currentText = current == null ? '-' : format(current);
  if (!futureAvailable) {
    return { indicator: label, current: currentText, future: FUTURE_MISSING, variation: '-' };
  }
  const futureText = future == null ? '-' : format(future);
  const variation = current == null || future == null ? '-' : projectedVariation(current, future, kind);
  return { indicator: label, current: currentText, future: futureText, variation };
}

function uniqueKey(paths: TechnicalPathRow[], key: string): boolean {
  return paths.filter((path) => path.key === key).length === 1;
}

export function buildTechnicalFlowReport(input: {
  processName: string;
  organizationName?: string | null;
  generatedAt?: Date;
  cards: EnterpriseCardInput[];
  futureCards?: EnterpriseCardInput[] | null;
}): TechnicalFlowReport {
  const currentFlow = analyzeFlow(input.cards);
  const futureAvailable = scenarioIsComparable(input.cards, input.futureCards);
  const futureFlow = futureAvailable && input.futureCards ? analyzeFlow(input.futureCards) : null;
  const currentEnum = enumerateFlowPaths(input.cards);
  const futureEnum = futureFlow && input.futureCards ? enumerateFlowPaths(input.futureCards) : { paths: [], truncated: false };
  const currentPaths = pathsOf(currentEnum.paths);
  const futurePaths = pathsOf(futureEnum.paths);

  const indicators: TechnicalIndicatorRow[] = [
    indicator('Atividades', currentFlow.activities, futureFlow?.activities ?? null, futureAvailable, 'count', (value) => String(value)),
    indicator('Decisões', currentFlow.decisions, futureFlow?.decisions ?? null, futureAvailable, 'count', (value) => String(value)),
    indicator('TP da rota principal', currentFlow.mainLeadMinutes, futureFlow?.mainLeadMinutes ?? null, futureAvailable, 'duration', (value) => formatExecutiveDuration(value)),
    indicator('TRA da rota principal', currentFlow.mainTouchMinutes, futureFlow?.mainTouchMinutes ?? null, futureAvailable, 'duration', (value) => formatExecutiveDuration(value)),
    indicator('Espera da rota principal', currentFlow.mainWaitMinutes, futureFlow?.mainWaitMinutes ?? null, futureAvailable, 'duration', (value) => formatExecutiveDuration(value)),
    indicator('PCE', currentFlow.mainPcePercent, futureFlow?.mainPcePercent ?? null, futureAvailable, 'points', (value) => percent(value)),
    indicator('Handoffs', currentFlow.handoffs, futureFlow?.handoffs ?? null, futureAvailable, 'count', (value) => String(value)),
    indicator('Retornos', currentFlow.returns, futureFlow?.returns ?? null, futureAvailable, 'count', (value) => String(value)),
  ];

  const comparison: TechnicalPathComparisonRow[] = [];
  if (futureAvailable) {
    const matched = new Set<string>();
    for (const path of currentPaths) {
      const reliable = Boolean(path.key) && uniqueKey(currentPaths, path.key) && futurePaths.filter((item) => item.key === path.key).length <= 1;
      const twin = reliable ? futurePaths.find((item) => item.key === path.key) : undefined;
      if (!reliable) {
        comparison.push({
          path: `${path.code} - ${path.title}`,
          tpCurrent: path.tp,
          traCurrent: path.tra,
          tpFuture: '-',
          traFuture: '-',
          variation: '-',
        });
        continue;
      }
      if (!twin) {
        comparison.push({
          path: `${path.code} - ${path.title}`,
          tpCurrent: path.tp,
          traCurrent: path.tra,
          tpFuture: '-',
          traFuture: '-',
          variation: ELIMINATED,
        });
        continue;
      }
      matched.add(path.key);
      comparison.push({
        path: `${path.code} - ${path.title}`,
        tpCurrent: path.tp,
        traCurrent: path.tra,
        tpFuture: twin.tp,
        traFuture: twin.tra,
        variation: path.tpMinutes == null || twin.tpMinutes == null
          ? '-'
          : projectedVariation(path.tpMinutes, twin.tpMinutes, 'duration'),
      });
    }
    for (const path of futurePaths) {
      if (!path.key || matched.has(path.key) || !uniqueKey(futurePaths, path.key)) continue;
      if (currentPaths.some((item) => item.key === path.key)) continue;
      comparison.push({
        path: `${path.code} - ${path.title}`,
        tpCurrent: '-',
        traCurrent: '-',
        tpFuture: path.tp,
        traFuture: path.tra,
        variation: CREATED,
      });
    }
  }

  return {
    processName: input.processName?.trim() || 'Processo sem nome',
    organizationName: input.organizationName?.trim() || null,
    generatedAt: (input.generatedAt ?? new Date()).toLocaleDateString('pt-BR'),
    futureAvailable,
    futureNote: futureAvailable ? null : FUTURE_MISSING,
    legend: 'TP é a permanência informada da atividade. TRA é o tempo de realização. Espera = TP - TRA quando os dois tempos existem.',
    indicators,
    currentActivities: activitiesOf(currentFlow),
    futureActivities: futureFlow ? activitiesOf(futureFlow) : [],
    currentPaths,
    futurePaths,
    pathComparison: comparison,
    pathsTruncated: currentEnum.truncated || futureEnum.truncated,
  };
}
